import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { CircularProgress } from '@mui/material';

import dayjs from 'shared/helpers/dayjs';
import routesConfig from 'constants/routesConfig.json';
import ErrorBoundary from 'layout/components/ErrorBoundary';
import { TopBar } from 'topBar/components/TopBar';
import { PriorityCard } from 'priorities/components/PriorityCard/_index';
import { PrioritySection } from 'priorities/components/PrioritySection/_index';
import { useGuidanceQuestions } from 'priorities/hooks/useGuidanceQuestions';
import { GuidanceQuestion } from 'priorities/_types';
import { useEvents } from 'events/hooks/useEvents';
import formatDateParam from 'events/helpers/dateFormat';
import {
  ShiftNoteCaptureProvider,
  useShiftNoteCapture,
} from 'shiftNotes/helpers/ShiftNoteCaptureProvider';
import {
  NoteCaptureSection,
  NoteCaptureInlineProcessing,
} from 'shiftNotes/components/NoteCapture/_index';
import { readStoredInputMethod } from 'settings/utils/inputMethodStorage';
import * as Text from 'theme/components/Text.style';
import * as Styled from 'priorities/styles/PrioritiesPage.style';

export interface EventSelectOptions {
  autoStartRecordingForQuestionId?: string;
}

interface PrioritiesPageProps {
  embedded?: boolean;
  selectedDate?: dayjs.Dayjs;
  onEventSelect?: (eventId: string, options?: EventSelectOptions) => void;
}

function PrioritiesPageContent({
  embedded,
  selectedDate,
  onEventSelect,
}: {
  embedded: boolean;
  selectedDate?: dayjs.Dayjs;
  onEventSelect?: (eventId: string, options?: EventSelectOptions) => void;
}): JSX.Element {
  const { t } = useTranslation('pagePriorities');
  const history = useHistory();
  const location = useLocation<{ selectedDate?: string } | undefined>();

  const { onRecordingToggle, openWritingMode } = useShiftNoteCapture();
  const { data: questions, isLoading } = useGuidanceQuestions();

  const startCaptureForQuestion = (questionId: string): void => {
    if (readStoredInputMethod() === 'keyboard') {
      openWritingMode(questionId);
    } else {
      onRecordingToggle(questionId);
    }
  };

  const effectiveDate = useMemo(() => {
    if (selectedDate) return selectedDate.startOf('day');
    const fromState = location.state?.selectedDate;
    if (fromState) {
      const parsed = dayjs(fromState);
      if (parsed.isValid()) return parsed.startOf('day');
    }
    return dayjs().startOf('day');
  }, [selectedDate, location.state]);

  const dateParam = useMemo(
    () => formatDateParam(effectiveDate),
    [effectiveDate]
  );
  const { data: eventsData } = useEvents(dateParam);
  const eventSummariesById = useMemo(() => {
    const map = new Map<string, string>();
    (eventsData?.results ?? []).forEach((event) => {
      const text = event.summary || event.shift_notes?.[0]?.edited_text;
      if (text) map.set(event.id, text);
    });
    return map;
  }, [eventsData]);

  const { routineQuestions, eventQuestions } = useMemo(() => {
    const routine: GuidanceQuestion[] = [];
    const event: GuidanceQuestion[] = [];
    (questions ?? [])
      .filter((question) =>
        dayjs(question.created).isSame(effectiveDate, 'day')
      )
      .forEach((question) => {
        if (question.event) {
          event.push(question);
        } else {
          routine.push(question);
        }
      });
    return { routineQuestions: routine, eventQuestions: event };
  }, [questions, effectiveDate]);

  return (
    <Styled.PageWrapper>
      {!embedded && (
        <TopBar
          onBackClick={() => history.goBack()}
          showBackButton
          backButtonAriaLabel={t('page.backAriaLabel')}
        />
      )}
      <Styled.Container>
        <Styled.TitleRow>
          <Text.H3>{t('page.title')}</Text.H3>
        </Styled.TitleRow>

        {isLoading ? (
          <CircularProgress />
        ) : (
          <>
            <PrioritySection
              title={t('page.sections.routine')}
              count={routineQuestions.length}
              collapseAriaLabel={t('page.sections.collapseAriaLabel', {
                section: t('page.sections.routine'),
              })}
              expandAriaLabel={t('page.sections.expandAriaLabel', {
                section: t('page.sections.routine'),
              })}
            >
              {routineQuestions.map((question) => (
                <PriorityCard
                  key={question.id}
                  variant="routine"
                  prompt={question.text}
                  onRecord={() => startCaptureForQuestion(question.id)}
                />
              ))}
            </PrioritySection>

            <PrioritySection
              title={t('page.sections.updateEvents')}
              count={eventQuestions.length}
              collapseAriaLabel={t('page.sections.collapseAriaLabel', {
                section: t('page.sections.updateEvents'),
              })}
              expandAriaLabel={t('page.sections.expandAriaLabel', {
                section: t('page.sections.updateEvents'),
              })}
            >
              {eventQuestions.map((question) => {
                const goToEvent = (options?: EventSelectOptions): void => {
                  if (!question.event) return;
                  if (onEventSelect) {
                    onEventSelect(question.event, options);
                    return;
                  }
                  history.push({
                    pathname: `${routesConfig.EVENT_PAGE}/${question.event}`,
                    state: {
                      eventsDate: dateParam,
                      ...(options?.autoStartRecordingForQuestionId
                        ? {
                            autoStartRecordingForQuestionId:
                              options.autoStartRecordingForQuestionId,
                          }
                        : {}),
                    },
                  });
                };
                return (
                  <PriorityCard
                    key={question.id}
                    variant="updateEvent"
                    prompt={question.text}
                    previousUpdate={
                      question.event
                        ? eventSummariesById.get(question.event)
                        : undefined
                    }
                    onRecord={() =>
                      goToEvent({
                        autoStartRecordingForQuestionId: question.id,
                      })
                    }
                    onClick={question.event ? () => goToEvent() : undefined}
                  />
                );
              })}
            </PrioritySection>
          </>
        )}
        <NoteCaptureInlineProcessing />
      </Styled.Container>
      <NoteCaptureSection mobileView suppressReady />
    </Styled.PageWrapper>
  );
}

export default function PrioritiesPage({
  embedded = false,
  selectedDate,
  onEventSelect,
}: PrioritiesPageProps): JSX.Element {
  return (
    <ErrorBoundary>
      <ShiftNoteCaptureProvider>
        <PrioritiesPageContent
          embedded={embedded}
          selectedDate={selectedDate}
          onEventSelect={onEventSelect}
        />
      </ShiftNoteCaptureProvider>
    </ErrorBoundary>
  );
}
